import { AsyncThunkAction, createSlice, PayloadAction } from "@reduxjs/toolkit"
import {
  cancelGetPricesRequest,
  CarModel,
  getEcommModelsId,
  getEcommPricesLicensingFee,
  getSettingsAll,
  LicensingFeeResponse,
  postEcommPricesWithCancellation,
  PriceItem,
} from "api"
import { getUtmParamsAsKeyValueList } from "common/bit/utils/utmParams"
import { setSelectedAccessories } from "components/shopFeatures/accessories/accessoriesSlice"
import { setSelectedColor } from "components/shopFeatures/color/colorSlice"
import {
  fetchDetailings,
  setSelectedDetailing,
} from "components/shopFeatures/detailing/detailingSlice"
import { setSelectedUpholstery } from "components/shopFeatures/upholstery/upholsterySlice"
import { createAsyncAppThunk, ThunkApiConfig } from "store"
import { buildQueryString, getQueryParams } from "./queryParams"

const name = "wizard"

export type WizardState = {
  model: CarModel | null
  price: PriceItem | null
  licensingFee: LicensingFeeResponse | null
  initialized: boolean
  isDesktop: boolean
  isDialogOpen: boolean
  showOnlyStock: boolean
  hideOutOfStockBanner: boolean
  advancePaymentAmount: number
}

const initialState: WizardState = {
  model: null,
  price: null,
  licensingFee: null,
  initialized: false,
  isDesktop: false,
  isDialogOpen: false,
  showOnlyStock: false,
  hideOutOfStockBanner: false,
  advancePaymentAmount: 200,
}

export const fetchModel = createAsyncAppThunk(
  `${name}/fetchModel`,
  async (modelId: string, { dispatch }) => {
    const { data } = await getEcommModelsId(modelId)

    await dispatch(fetchDetailings({ modelId }))

    return data.body.details
  }
)

export const updatePriceAndURL = createAsyncAppThunk(
  `${name}/updatePriceAndURL`,
  async (_, { getState }) => {
    const {
      detailing: { selectedDetailingCode: detailing },
      color: { selectedColorCode: color },
      upholstery: { selectedUpholsteryCode: upholstery },
      accessories: { selectedAccessoriesCodes: accessories },
    } = getState()

    // Cancel the previous request
    cancelGetPricesRequest?.()
    // getEcommPrices
    // getPricesWithCancellation
    if (!detailing) {
      return null
    }
    const { data } = await postEcommPricesWithCancellation({
      finishingLevelCode: detailing,
      colorCode: color,
      upholsteryCode: upholstery,
      utms: getUtmParamsAsKeyValueList(),
      carExtraCodes: accessories,
      sid: window?.__ctm?.config?.sid || "",
    })

    if (typeof window !== "undefined") {
      // Build URL params
      const values = { detailing, color, upholstery, accessories }
      const params = buildQueryString(values)

      // Replace URL and preserve hash

      const url = `?${params}${location.hash}`
      const currentUrl = window.location.search + window.location.hash
      if (currentUrl !== url) {
        history.replaceState(null, document.title, url)
      }
    }

    return data.body
  }
)

export const updateLicensingFee = createAsyncAppThunk(
  `${name}/updateLicensingFee`,
  async (code: string) => {
    const { data } = await getEcommPricesLicensingFee(code)
    return data.body
  }
)

export const fetchAdvancePaymentAmount = createAsyncAppThunk(
  `${name}/fetchAdvancePaymentAmount`,
  async () => {
    const { data } = await getSettingsAll()
    // data.body is GeneralSetting[]
    const found = data.body.find(
      (setting) => setting.key === "AdvancePaymentAmount"
    )
    const value = found && found.value ? Number(found.value) : 200
    return isNaN(value) ? 200 : value
  }
)

export const initSelectedComponentsFromParams = createAsyncAppThunk(
  `${name}/initSelectedComponentsFromParams`,
  async (modelId: string, { getState, dispatch }) => {
    const params = getQueryParams()
    const firstDetailingCode =
      getState().detailing.engines[0].finishingLevels[0].details.code

    // Prepare the `setSelected*` actions for each of the provided params
    const actions: Array<AsyncThunkAction<any, any, ThunkApiConfig> | null> = [
      // Re-fetch the detailings in case there's a discount for authenticated users
      fetchDetailings({ modelId }),

      setSelectedDetailing(params.detailing || firstDetailingCode),
      params.color ? setSelectedColor(params.color) : null,
      params.upholstery ? setSelectedUpholstery(params.upholstery) : null,
      params.accessories && params.accessories.length > 0
        ? setSelectedAccessories(params.accessories)
        : null,
    ]

    await Promise.allSettled(
      actions.filter((action) => action !== null).map(dispatch)
    )
  }
)

const wizard = createSlice({
  name,
  initialState,
  reducers: {
    setModel(state, { payload }: PayloadAction<WizardState["model"]>) {
      state.model = payload
    },
    setInitialized(
      state,
      { payload }: PayloadAction<WizardState["initialized"]>
    ) {
      state.initialized = payload
    },
    setIsDesktop(state, { payload }: PayloadAction<WizardState["isDesktop"]>) {
      state.isDesktop = payload
    },
    setIsDialogOpen(
      state,
      { payload }: PayloadAction<WizardState["isDialogOpen"]>
    ) {
      state.isDialogOpen = payload
    },
    // Add reducer for toggling showOnlyStock
    toggleShowOnlyStock(state) {
      state.showOnlyStock = !state.showOnlyStock
    },
    // Add reducer for hiding out of stock banner
    setHideOutOfStockBanner(state, { payload }: PayloadAction<boolean>) {
      state.hideOutOfStockBanner = payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchModel.fulfilled, (state, { payload }) => {
        state.model = payload
      })
      .addCase(updatePriceAndURL.fulfilled, (state, { payload }) => {
        state.price = payload
      })
      .addCase(updateLicensingFee.fulfilled, (state, { payload }) => {
        state.licensingFee = payload
      })
      .addCase(fetchAdvancePaymentAmount.fulfilled, (state, { payload }) => {
        state.advancePaymentAmount = payload
      })
  },
})

export const {
  setModel,
  setInitialized,
  setIsDesktop,
  setIsDialogOpen,
  toggleShowOnlyStock,
  setHideOutOfStockBanner,
} = wizard.actions

export default wizard.reducer
